import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/PasswordInput';
import { Label } from '@/components/ui/label';
import { Lock } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function Settings() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.new_password !== formData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (formData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        old_password: formData.old_password,
        new_password: formData.new_password
      });
      toast.success('Password changed successfully!');
      setFormData({ old_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex">
      <Sidebar />
      <div className="ml-64 flex-1 p-8" data-testid="settings-page">
        <div className="max-w-2xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-foreground mb-2">Settings</h1>
            <p className="text-stone-600">Manage your account settings</p>
          </div>

          <Card className="border-stone-200 shadow-card mb-6">
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-stone-600">Username</p>
                <p className="font-medium" data-testid="username">{user?.username || 'Loading...'}</p>
              </div>
              <div>
                <p className="text-sm text-stone-600">Role</p>
                <p className="font-medium capitalize" data-testid="role">{user?.role || 'Loading...'}</p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-stone-200 shadow-card">
            <CardHeader>
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-primary" />
                <div>
                  <CardTitle>Change Password</CardTitle>
                  <CardDescription>Update your password to keep your account secure</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="old_password">Current Password *</Label>
                  <PasswordInput
                    id="old_password"
                    data-testid="old-password-input"
                    value={formData.old_password}
                    onChange={(e) => setFormData({ ...formData, old_password: e.target.value })}
                    required
                    className="h-11"
                  />
                </div>
                <div>
                  <Label htmlFor="new_password">New Password *</Label>
                  <PasswordInput
                    id="new_password"
                    data-testid="new-password-input"
                    value={formData.new_password}
                    onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                    required
                    className="h-11"
                    minLength={6}
                  />
                  <p className="text-xs text-stone-500 mt-1">Must be at least 6 characters</p>
                </div>
                <div>
                  <Label htmlFor="confirm_password">Confirm New Password *</Label>
                  <PasswordInput
                    id="confirm_password"
                    data-testid="confirm-password-input"
                    value={formData.confirm_password}
                    onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                    required
                    className="h-11"
                    minLength={6}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full rounded-full"
                  disabled={loading}
                  data-testid="change-password-button"
                >
                  {loading ? 'Changing Password...' : 'Change Password'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}